/**
 * ChainSelector — global blockchain picker for the SYNAPZ Launch Network.
 *
 * Shows the active chain in compact form and opens a panel listing all
 * supported and planned networks. Unavailable networks show roadmap info
 * only — no launch buttons are offered for undeployed chains.
 */

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  NETWORK_REGISTRY,
  STATUS_LABELS,
  STATUS_TONES,
  PRODUCT_LABELS,
  ACTIVE_NETWORK,
  type NetworkEntry,
  type NetworkStatus,
} from "../config/networks";

// ── Chain icon ────────────────────────────────────────────────────────────────

function ChainIcon({ network, size = 24 }: { network: NetworkEntry; size?: number }) {
  const r = size / 2;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      role="img"
    >
      <circle cx={r} cy={r} r={r} fill={network.accentColor} fillOpacity="0.15" />
      <circle cx={r} cy={r} r={r - 2} fill="none" stroke={network.accentColor} strokeWidth="1.2" strokeOpacity="0.6" />
      <text
        x={r}
        y={r + (size * 0.14)}
        textAnchor="middle"
        fontSize={size * 0.42}
        fontWeight="700"
        fontFamily="monospace"
        fill={network.accentColor}
      >
        {network.shortName.slice(0, 2).toUpperCase()}
      </text>
    </svg>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

const TONE_CLASSES: Record<string, string> = {
  plasma: "text-plasma border-plasma/30 bg-plasma/8",
  warn:   "text-warn border-warn/30 bg-warn/8",
  danger: "text-danger border-danger/30 bg-danger/8",
  muted:  "text-muted border-line/60 bg-line/20",
};

function StatusBadge({ status }: { status: NetworkStatus }) {
  const tone = STATUS_TONES[status];
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-mono font-bold tracking-wider border ${TONE_CLASSES[tone]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ── Network card (in dropdown panel) ─────────────────────────────────────────

function NetworkCard({ network, onClose }: { network: NetworkEntry; onClose: () => void }) {
  const isActive = network.key === ACTIVE_NETWORK.key;
  const products = [
    ...network.products.map((p) => PRODUCT_LABELS[p]),
    ...network.plannedProducts.map((p) => `${PRODUCT_LABELS[p]} planned`),
  ];

  return (
    <div
      className={`flex items-start gap-3 rounded-lg p-3 border transition-colors ${
        isActive
          ? "border-plasma/30 bg-plasma/5"
          : "border-line/40 bg-panel/30 hover:bg-panel/60"
      }`}
      style={isActive ? { background: "rgba(0,229,199,0.04)" } : undefined}
    >
      {/* Icon */}
      <div className="shrink-0 mt-0.5">
        <ChainIcon network={network} size={28} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <span className="text-sm font-semibold text-ink leading-none">
            {network.name}
          </span>
          {isActive && (
            <span className="text-[9px] font-mono text-plasma/80">← current</span>
          )}
        </div>
        <StatusBadge status={network.status} />
        {products.length > 0 && (
          <p className="text-[11px] text-muted mt-1.5 leading-snug">
            {products.join(" · ")}
          </p>
        )}
        <p className="text-[10px] text-muted/55 mt-1 leading-snug font-mono">
          {network.nativeAssetSymbol} · {network.vm.toUpperCase()}
        </p>
      </div>

      {/* Action */}
      <div className="shrink-0">
        {network.launchEnabled ? (
          <Link
            to={network.slug ? `/launch/${network.slug}` : "/create"}
            onClick={onClose}
            className="text-[11px] font-semibold text-plasma hover:text-plasma/75 transition-colors"
          >
            Launch →
          </Link>
        ) : (
          <Link
            to="/networks"
            onClick={onClose}
            className="text-[11px] text-muted/60 hover:text-muted transition-colors"
          >
            Roadmap →
          </Link>
        )}
      </div>
    </div>
  );
}

// ── Main selector component ───────────────────────────────────────────────────

export function ChainSelector() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Choose blockchain"
        className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
          open
            ? "border-plasma/40 bg-plasma/8 text-plasma"
            : "border-line/60 bg-panel/50 text-muted hover:border-line hover:text-ink"
        }`}
        style={open ? { background: "rgba(0,229,199,0.07)" } : undefined}
      >
        <ChainIcon network={ACTIVE_NETWORK} size={16} />
        <span className="hidden sm:inline max-w-[100px] truncate">{ACTIVE_NETWORK.shortName}</span>
        <StatusBadge status={ACTIVE_NETWORK.status} />
        {/* Chevron */}
        <svg
          aria-hidden="true"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={`shrink-0 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        >
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Choose blockchain"
          className="absolute right-0 top-full mt-1.5 w-72 sm:w-80 z-50 rounded-xl border border-line/70 bg-void/95 backdrop-blur-md shadow-xl shadow-void/50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-line/50">
            <span className="text-xs font-semibold text-ink">Choose Blockchain</span>
            <Link
              to="/networks"
              onClick={() => setOpen(false)}
              className="text-[10px] text-muted/70 hover:text-plasma transition-colors"
            >
              Full roadmap →
            </Link>
          </div>

          {/* Network list */}
          <div className="max-h-[min(420px,60vh)] overflow-y-auto p-2 space-y-1">
            {NETWORK_REGISTRY.map((network) => (
              <NetworkCard
                key={network.key}
                network={network}
                onClose={() => setOpen(false)}
              />
            ))}
          </div>

          {/* Footer note */}
          <div className="px-4 py-2.5 border-t border-line/50 bg-panel/20">
            <p className="text-[10px] text-muted/55 leading-snug">
              Only live networks support token creation. Unavailable networks show roadmap information only.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
